/**
 * The review of a refinement as a draft kept in this browser (`ui/draftStorage`, per person and refinement): which
 * changes are taken, the person's edits of the proposed text, and the changes the asset's own text moved under
 * (named by a 409 on apply) with the asset's text the person then reviewed. It turns into the one apply body, which
 * sends that reviewed text as `current` so the server compares the live text with what the person saw. Nothing is on the asset until Apply.
 */

import type { AssetOut } from '@/api/types/assets';
import type { RefinementApplyIn, RefineChange, RefineProposalOut } from '@/api/types/refinements';
import { draftKey, readDraft, writeDraft } from '@/ui/draftStorage';

export interface RefineDraft {
  extractionId: string;
  /** The change ids to apply, in the proposal's order. */
  taken: string[];
  /** The person's text for an editable change, by id; a change without one goes as proposed. */
  edits: Record<string, string>;
  /** Change ids whose live text moved since the refinement (a 409 on apply named them). */
  moved: string[];
  /** For a moved change, the asset's text the page showed after reloading (null: the section is gone), by id. */
  reviewed: Record<string, string | null>;
}

/** Fields whose proposed text the person may edit; every section is editable too. */
const EDITABLE_FIELDS = new Set(['tagline', 'summary']);

export function isEditable(change: Pick<RefineChange, 'target' | 'key'>): boolean {
  return change.target === 'section' || EDITABLE_FIELDS.has(change.key);
}

/** A list field (industries, partners, countries): names one per line, with what it adds and removes. */
export function isListChange(change: Pick<RefineChange, 'add' | 'remove'>): boolean {
  return change.add.length > 0 || change.remove.length > 0;
}

/** Every change starts taken: the review is of a proposal the person asked for, and Clear is one click. */
export function draftFromRefinement(proposal: RefineProposalOut): RefineDraft {
  return { extractionId: proposal.id, taken: proposal.changes.map((c) => c.id), edits: {}, moved: [], reviewed: {} };
}

export const isRefineDraftOf =
  (extractionId: string) =>
  (value: unknown): value is RefineDraft => {
    const draft = value as Partial<RefineDraft> | null;
    return draft?.extractionId === extractionId && Array.isArray(draft.taken) && Array.isArray(draft.moved) && typeof draft.reviewed === 'object' && draft.reviewed !== null;
  };

/** The text a change applies: the person's edit, else the proposal's. */
export function textOf(draft: RefineDraft, change: RefineChange): string {
  return draft.edits[change.id] ?? change.proposed;
}

/** The asset's text now for a text change (a section that does not exist is null); list changes never move. */
export function liveText(asset: AssetOut, change: RefineChange): string | null {
  if (change.target === 'section') return asset.sections.find((s) => s.key === change.key)?.body ?? null;
  if (change.key === 'tagline' || change.key === 'summary' || change.key === 'maturity') return asset[change.key] ?? null;
  return change.current;
}

/**
 * What the current pane shows: the proposal's starting text; once it moved, the asset's text the person reviewed
 * (or, until the reload answers, the asset's text as the page has it).
 */
export function currentOf(asset: AssetOut, draft: RefineDraft, change: RefineChange): string | null {
  if (change.id in draft.reviewed) return draft.reviewed[change.id] ?? null;
  return draft.moved.includes(change.id) ? liveText(asset, change) : change.current;
}

/** The draft once a 409 named `ids` and the asset was read again: those changes now compare with its text. */
export function withReviewed(draft: RefineDraft, proposal: RefineProposalOut, asset: AssetOut, ids: string[]): RefineDraft {
  const named = proposal.changes.filter((c) => ids.includes(c.id));
  return {
    ...draft,
    moved: [...new Set([...draft.moved, ...ids])],
    reviewed: { ...draft.reviewed, ...Object.fromEntries(named.map((c) => [c.id, liveText(asset, c)])) },
  };
}

export function applyBody(proposal: RefineProposalOut, draft: RefineDraft, revision: number): RefinementApplyIn {
  const taken = new Set(draft.taken);
  return {
    revision,
    changes: proposal.changes
      .filter((c) => taken.has(c.id))
      .map((c) => {
        const edited = draft.edits[c.id];
        const change: RefinementApplyIn['changes'][number] = { id: c.id };
        if (isEditable(c) && edited !== undefined && edited !== c.proposed) change.proposed = edited;
        if (c.id in draft.reviewed) change.current = draft.reviewed[c.id] ?? null;
        return change;
      }),
  };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "5 changes · 2 gaps" (gaps only when there are some). */
export function summaryText(proposal: RefineProposalOut): string {
  const changes = plural(proposal.changes.length, 'change', 'changes');
  return proposal.gaps.length > 0 ? `${changes} · ${plural(proposal.gaps.length, 'gap', 'gaps')}` : changes;
}

export function applyLabel(count: number): string {
  return `Apply ${plural(count, 'change', 'changes')}`;
}

// --- Kept in this browser ------------------------------------------------------------------------------------------

export const refineDraftKey = (userId: string, extractionId: string) => draftKey(userId, `refine-${extractionId}`, 1);

/**
 * The instruction a refinement was queued with, kept so **Try again** after a failure can offer it again: the status
 * the page polls does not carry it, and a failed refinement has no proposal to read it from.
 */
const instructionKey = (userId: string, extractionId: string) => draftKey(userId, `refine-instruction-${extractionId}`, 1);

export function keepInstruction(userId: string, extractionId: string, instruction: string): void {
  writeDraft(instructionKey(userId, extractionId), instruction);
}

export function keptInstruction(userId: string, extractionId: string): string {
  return readDraft(instructionKey(userId, extractionId)) ?? '';
}
